import {Injectable} from "@angular/core";
import {catchError, filter, map, of, switchMap} from "rxjs";
import {Store} from "@ngrx/store";
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import * as TrunkNoticeActions from "./trunk-notice.actions";
import {getTrunkNoticeQuery, getSelected} from "./trunk-notice.selectors";
import {NEW_ENTITY} from "../../../utility/constants/new-entity.constants";
import { onNavigation } from '../../../+state/on-navigation.operator';
import {TrunkNoticesComponent} from "../trunk-notices.component";
import {TrunkNoticeService} from "../../../utility/services/trunk-notice.service";
import {TrashComponent} from "../../../trash/trash.component";
import { getTrashState } from '../../../+state/root.selectors';
import { StoreRootState } from '../../../+state/root.reducer';

@Injectable()
export class TrunkNoticeEffects {
  trunkNoticeIndex$ = createEffect(() =>
    this.actions$.pipe(
      onNavigation(TrunkNoticesComponent),
      map( () => TrunkNoticeActions.loadTrunkNotices())
    )
  );

  trunkNoticeTrash$ = createEffect(() =>
    this.actions$.pipe(
      onNavigation(TrashComponent),
      map( () => TrunkNoticeActions.loadTrunkNotices())
    )
  );

  trunkNoticeView$ = createEffect(() =>
    this.actions$.pipe(
      onNavigation(TrunkNoticesComponent),
      filter( action => action.payload.routerState.params['trunkNoticeId'] !== NEW_ENTITY),
      map( (action) => {
        const trunkNoticeId = parseInt(action.payload.routerState.params['trunkNoticeId'], 10);

        if (!trunkNoticeId) {
          throw new Error('trunkNotice id is missing');
        }

        return TrunkNoticeActions.setSelectedTrunkNotice({ id: trunkNoticeId });
      })
    )
  );

  selectTrunkNotice$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TrunkNoticeActions.setSelectedTrunkNotice),
        concatLatestFrom(() => this.store.select(getSelected)),
        switchMap(([action, selected]) => {
          if (!selected) {
            return this.trunkNoticeService.loadTrunkNotices().pipe(
              map(res => TrunkNoticeActions.loadTrunkNoticeDetailSuccess({ trunkNotice: res.items[0] }))
            );
          }

          return of(TrunkNoticeActions.loadTrunkNoticeDetailUnneeded());
        }),
        catchError((error) => {
          return of(TrunkNoticeActions.loadTrunkNoticesFailure({ error }));
        })
      )
  );

  loadTrunkNotice$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TrunkNoticeActions.loadTrunkNotices),
        concatLatestFrom(() => [
          this.store.select(getTrunkNoticeQuery),
          this.store.select(getTrashState)
        ]),
        switchMap(([action, query, trashState]) => {
          return this.trunkNoticeService.loadTrunkNotices(query + '&deleted=' + trashState).pipe(
            map(res => TrunkNoticeActions.loadTrunkNoticesSuccess({ trunkNotice: res.items, total: res.meta.totalItems })),
          );
        }),
        catchError((error) => {
          return of(TrunkNoticeActions.loadTrunkNoticesFailure({ error }));
        })
      )
  );

  setTrunkNoticeFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        TrunkNoticeActions.setTrunkNoticePage,
        TrunkNoticeActions.setTrunkNoticeTerm,
        TrunkNoticeActions.setTrunkNoticeSort
      ),
      map(() => TrunkNoticeActions.loadTrunkNotices())
    )
  );

  createTrunkNotice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrunkNoticeActions.createTrunkNotice),
      switchMap((action) => {
        return this.trunkNoticeService.saveTrunkNotice(action.trunkNotice).pipe(
          map(created =>
            TrunkNoticeActions.saveTrunkNoticeSuccess( { trunkNotice: created, insert: true })
          ),
          catchError(error => {
            return of(TrunkNoticeActions.saveTrunkNoticeFailure({ error }));
          })
        )
      })
    )
  );

  updateTrunkNotice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrunkNoticeActions.updateTrunkNotice),
      switchMap((action) => {
        return this.trunkNoticeService.saveTrunkNotice(action.trunkNotice).pipe(
          map(updated =>
            TrunkNoticeActions.saveTrunkNoticeSuccess( { trunkNotice: updated, insert: false })
          ),
          catchError(error => {
            return of(TrunkNoticeActions.saveTrunkNoticeFailure({ error }));
          })
        )
      })
    )
  );

  deleteTrunkNotice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrunkNoticeActions.deleteTrunkNotice),
      switchMap((action) => {
        return this.trunkNoticeService.deleteTrunkNotice(action.trunkNotice.id).pipe(
          map(() =>
            TrunkNoticeActions.deleteTrunkNoticeSuccess( { id: action.trunkNotice.id })
          ),
          catchError(error => {
            return of(TrunkNoticeActions.deleteTrunkNoticeFailure({ error }));
          })
        )
      })
    )
  );

  restoreTrunkNotice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrunkNoticeActions.restoreTrunkNotice),
      switchMap((action) => {
        return this.trunkNoticeService.restoreTrunkNotice(action.element).pipe(
          map(response =>
            TrunkNoticeActions.restoreTrunkNoticeSuccess( { trunkNotice: response })
          ),
          catchError(error => {
            return of(TrunkNoticeActions.restoreTrunkNoticeFailure({ error }));
          })
        )
      })
    )
  );

  constructor(private readonly actions$: Actions,
              private trunkNoticeService: TrunkNoticeService,
              private store: Store<StoreRootState>) {
  }
}
